<script setup lang="ts">
import { computed } from 'vue'
import BaseInput from '@/components/ui/BaseInput.vue'
import { CAPTCHA_LENGTH } from '@/contracts/captcha.contract'

/**
 * 驗證碼欄位：輸入框 + 圖片 + 換一張。
 *
 * 展示元件 —— 圖片與重新整理都由父層透過 props/events 提供。
 *
 * SVG 以 **`<img>` + data URI** 呈現，而不是 `v-html` 內嵌。
 * 透過 `<img>` 載入的 SVG 被瀏覽器視為圖片，裡面的 script 與外部參照
 * 一律不執行 —— 就算後端哪天被塞了東西進來，也不會變成 XSS。
 * 內嵌 SVG 雖然可以用 CSS 上色，但這裡不需要，不值得換一個攻擊面。
 */
const props = defineProps<{
  /** 後端回傳的 SVG 字串。`null` 代表還沒取到 */
  svg: string | null
  loading: boolean
  failed: boolean
  error: string | null
  disabled: boolean
}>()

defineEmits<{ refresh: [] }>()

const model = defineModel<string>({ required: true })

/**
 * `encodeURIComponent` 而不是 base64：`btoa` 對非 ASCII 會直接丟例外，
 * 而 URI 編碼沒有這個限制，長度差異在這種尺寸下可以忽略。
 */
const imageSrc = computed(() =>
  props.svg === null ? null : `data:image/svg+xml;utf8,${encodeURIComponent(props.svg)}`,
)
</script>

<template>
  <div class="captcha">
    <BaseInput
      v-model="model"
      :label="$t('auth.captcha')"
      :placeholder="$t('auth.captchaPlaceholder', { length: CAPTCHA_LENGTH })"
      :error="error"
      type="tel"
      inputmode="numeric"
      autocomplete="off"
      :maxlength="CAPTCHA_LENGTH"
      :disabled="disabled"
    />

    <!-- 點圖換一張是這類表單的通用習慣，同時保留一顆有文字標籤的按鈕給鍵盤與讀屏 -->
    <button
      type="button"
      class="captcha__image"
      :aria-label="$t('auth.captchaRefresh')"
      :disabled="disabled || loading"
      @click="$emit('refresh')"
    >
      <img v-if="imageSrc !== null" class="captcha__svg" :src="imageSrc" alt="" />
      <span v-else-if="failed" class="captcha__fallback">{{ $t('auth.captchaRefresh') }}</span>
      <span v-else class="captcha__fallback">{{ $t('common.loading') }}</span>
    </button>
  </div>
</template>

<style scoped lang="scss">
.captcha {
  display: grid;
  // 輸入框佔滿剩餘寬度，圖片維持固定尺寸不被壓扁
  grid-template-columns: 1fr auto;
  align-items: start;
  gap: $space-3;

  &__image {
    // 與輸入框底線對齊：label 高度 + gap
    margin-top: calc(#{$text-sm} * 1.4 + #{$space-2});
    display: grid;
    place-items: center;
    width: 120px;
    height: 48px;
    padding: 0;
    border: 1px solid var(--panel-border, #{$color-border});
    border-radius: $radius-md;
    background: var(--panel-input, #{$color-surface-input});
    overflow: hidden;
    cursor: pointer;
    transition: border-color $transition-fast;

    &:hover:not(:disabled) {
      border-color: $color-text-muted;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &__svg {
    display: block;
    width: 118px;
    height: auto;
  }

  &__fallback {
    padding: 0 $space-2;
    color: $color-text-muted;
    font-size: $text-xs;
    text-align: center;
  }
}
</style>
